/**
 * M5T Chat Prompt Web Component
 * User message bubble (right-aligned)
 *
 * Shared component - can be used across multiple client apps
 *
 * Attributes:
 * - message: string - The message text to display
 * - timestamp: string - Timestamp to display (optional, defaults to current time)
 * - show-timestamp: boolean - Whether to show timestamp (default: true)
 *
 * Usage:
 *   <m5t-chat-prompt message="Hello, how are you?" timestamp="10:32 AM"></m5t-chat-prompt>
 *
 * JavaScript:
 *   const prompt = document.createElement('m5t-chat-prompt');
 *   prompt.setAttribute('message', 'Hello, how are you?');
 *   document.body.appendChild(prompt);
 */

class M5TChatPrompt extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
  }

  static get observedAttributes() {
    return ['message', 'timestamp', 'show-timestamp'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.render();
    }
  }

  /**
   * Get message text
   */
  getMessage() {
    return this.getAttribute('message') || '';
  }

  /**
   * Get timestamp
   */
  getTimestamp() {
    const timestamp = this.getAttribute('timestamp');
    if (timestamp) {
      return timestamp;
    }

    // Generate current time if not provided
    return new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  /**
   * Check if timestamp should be shown
   */
  shouldShowTimestamp() {
    return this.getAttribute('show-timestamp') !== 'false';
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Render the component
   */
  render() {
    const message = this.getMessage();
    const timestamp = this.getTimestamp();
    const showTimestamp = this.shouldShowTimestamp();

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: flex;
          flex-direction: column;
          align-self: flex-end;
          max-width: 70%;
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .bubble-content {
          background: #92a8d1;
          color: #ffffff;
          border-radius: 18px 18px 4px 18px;
          padding: 0.875rem 1.25rem;
          font-size: 0.95rem;
          line-height: 1.5;
          word-wrap: break-word;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .bubble-timestamp {
          font-size: 0.75rem;
          color: #a8a8a8;
          margin-top: 0.25rem;
          padding: 0 0.5rem;
          text-align: right;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          :host {
            max-width: 85%;
          }

          .bubble-content {
            padding: 0.75rem 1rem;
            font-size: 0.9rem;
          }
        }
      </style>

      <div class="bubble-content">${this.escapeHtml(message)}</div>
      ${showTimestamp ? `<div class="bubble-timestamp">${this.escapeHtml(timestamp)}</div>` : ''}
    `;
  }
}

// Define the custom element
customElements.define('m5t-chat-prompt', M5TChatPrompt);

export default M5TChatPrompt;
